import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import css from './Botao.module.css';
import { cx } from './cx';

/**
 * `primario` é a ação principal da tela — uma por área. `secundario` é o botão
 * comum. `discreto` não tem moldura: reserve para ação marginal (fechar,
 * "mostrar senha", ação em linha de tabela). `perigo` para destruir algo;
 * `perigoSolido` só dentro da confirmação.
 *
 * `contorno` é o nome antigo de `secundario` e continua aceito.
 */
export type Aparencia =
  | 'primario'
  | 'destaque'
  | 'secundario'
  | 'contorno'
  | 'discreto'
  | 'perigo'
  | 'perigoSolido';

interface PropsVisuais {
  aparencia?: Aparencia;
  pequeno?: boolean;
  grande?: boolean;
  larguraTotal?: boolean;
  /** Ícone à esquerda (lucide-react). */
  icone?: ReactNode;
  /** Ícone à direita — seta de "avançar", chevron de menu. */
  iconeDireita?: ReactNode;
  /** Só o ícone: exige `aria-label`. */
  soIcone?: boolean;
}

function classes(p: PropsVisuais, extra?: string) {
  return cx(
    css.botao,
    css[p.aparencia ?? 'primario'],
    p.pequeno && css.pequeno,
    p.grande && css.grande,
    p.larguraTotal && css.larguraTotal,
    p.soIcone && css.soIcone,
    extra,
  );
}

interface PropsBotao
  extends PropsVisuais,
    ButtonHTMLAttributes<HTMLButtonElement> {
  carregando?: boolean;
}

export function Botao({
  aparencia,
  pequeno,
  grande,
  larguraTotal,
  icone,
  iconeDireita,
  soIcone,
  carregando = false,
  disabled,
  className,
  children,
  ...resto
}: PropsBotao) {
  return (
    <button
      {...resto}
      // Sem `type` o navegador assume "submit" e qualquer botão dentro de um
      // <form> envia o formulário sem querer.
      type={resto.type ?? 'button'}
      disabled={disabled || carregando}
      aria-busy={carregando || undefined}
      className={classes(
        { aparencia, pequeno, grande, larguraTotal, soIcone },
        className,
      )}
    >
      {carregando ? <span className={css.girador} aria-hidden="true" /> : icone}
      {children}
      {iconeDireita}
    </button>
  );
}

/**
 * Link com cara de botão. Use isto em vez de `<Link><Botao/></Link>`, que põe
 * um botão dentro de um link — HTML inválido, e o leitor de tela anuncia dois
 * controles onde há um.
 */
export function BotaoLink({
  aparencia,
  pequeno,
  grande,
  larguraTotal,
  icone,
  iconeDireita,
  soIcone,
  className,
  children,
  ...resto
}: PropsVisuais & LinkProps) {
  return (
    <Link
      {...resto}
      className={classes(
        { aparencia, pequeno, grande, larguraTotal, soIcone },
        className,
      )}
    >
      {icone}
      {children}
      {iconeDireita}
    </Link>
  );
}

export function Girador({ tamanho = 14 }: { tamanho?: number }) {
  return (
    <span
      className={css.girador}
      aria-hidden="true"
      style={{ width: tamanho, height: tamanho }}
    />
  );
}
